/* =============================================================================
 * CoachEngagementCard — Phase 1 monetization surface (plan §1.2 / §1.3)
 * -----------------------------------------------------------------------------
 * Free-to-paid teaser sourced from GET /engagement/coaches. Free users see the
 * count only ("N coaches engaged — see who"); paid users see identities. The
 * server is the moat (free responses carry no coach identity); this card just
 * renders whichever tier the API returned. Renders nothing until there is real
 * engagement — no sad empty state on the dashboard.
 * ========================================================================== */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, ChevronRight } from 'react-feather';
import { useAuth } from '../contexts/AuthContext';
import { useSubscriptionGuard } from '../hooks/useSubscriptionGuard';
import { engagementAPI, CoachEngagementSummary } from '../services/api';
import { Card, Button } from './ui';
import { font } from '../styles/tokens';

interface CoachEngagementCardProps {
  /** Athlete profile id (guardian view / explicit athlete). Omit for own profile. */
  athleteId?: string;
}

const CoachEngagementCard: React.FC<CoachEngagementCardProps> = ({ athleteId }) => {
  const { sessionToken } = useAuth();
  const { requirePaid } = useSubscriptionGuard();
  const navigate = useNavigate();
  const [data, setData] = useState<CoachEngagementSummary | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!sessionToken) return;
    let cancelled = false;
    engagementAPI
      .getCoaches(sessionToken, athleteId)
      .then((d) => { if (!cancelled) { setData(d); setLoaded(true); } })
      .catch(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [sessionToken, athleteId]);

  // No engagement yet (or still loading / errored) → render nothing.
  if (!loaded || !data || data.count === 0) return null;

  const { count, this_week, isPaid, coaches } = data;
  const verb = count === 1 ? 'coach has' : 'coaches have';
  const named = (coaches || []).map((c) => c.coach_name).filter(Boolean) as string[];

  return (
    <Card tone="success" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div
          aria-hidden
          style={{
            width: 40, height: 40, borderRadius: 999, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--aps-success-tint)', color: 'var(--aps-success)',
          }}
        >
          <Eye size={20} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: font.weight.bold as any, color: 'var(--aps-ink)', lineHeight: 1.35 }}>
            {count} {verb} engaged with your outreach
            {this_week > 0 && (
              <span style={{ color: 'var(--aps-success-text)', fontWeight: font.weight.semibold as any }}>
                {' '}· {this_week} this week
              </span>
            )}
          </div>

          {isPaid ? (
            <>
              {named.length > 0 && (
                <div style={{ fontSize: 13, color: 'var(--aps-text-2)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {named.slice(0, 3).join(' · ')}
                  {named.length > 3 ? ` and ${named.length - 3} more` : ''}
                </div>
              )}
              <Button
                variant="ghost"
                size="sm"
                rightIcon={<ChevronRight size={14} aria-hidden />}
                onClick={() => navigate('/messages')}
                style={{ marginTop: 8, paddingLeft: 0 }}
              >
                View in Messages
              </Button>
            </>
          ) : (
            <>
              <div style={{ fontSize: 13, color: 'var(--aps-text-2)', marginTop: 4 }}>
                Upgrade to see which coaches opened your emails.
              </div>
              <Button
                variant="primary"
                size="sm"
                rightIcon={<ChevronRight size={14} aria-hidden />}
                onClick={() => requirePaid('See which coaches engaged with you', () => {})}
                style={{ marginTop: 8 }}
              >
                See who
              </Button>
            </>
          )}
        </div>
      </div>
    </Card>
  );
};

export default CoachEngagementCard;
